import { track } from '../services/analytics';
import React, { createContext, useContext, useReducer, useEffect, useRef, useState, useCallback } from 'react';
import { useAuth, PLAN_STORAGE_KEY } from './AuthContext';
import { DEFAULT_PROFILE, BLANK_PROFILE, STARTER_PROFILE, DEFAULT_CONFIG, DEFAULT_DRAWDOWN_PHASES } from '../config/planDefaults';
import { useEntitlement } from './EntitlementContext';
import {
  normalizePlan, resolveFirstSync, reconcileAccountCopy, addBackup, readBackups, PLAN_SCHEMA_VERSION,
} from '../utils/planSync';
import { createPlan, switchPlan, renamePlan, deletePlan } from '../utils/planLibrary';

const FinancialPlanContext = createContext(null);

// Where plans live on the device:
//   PLAN_STORAGE_KEY            the device plan (signed out, or signed in without sync)
//   PLAN_STORAGE_KEY:<user id>  a synced account's working copy, with unsaved-change marker
//   PLAN_STORAGE_KEY:backup:<user id>  that account's plans that were replaced by a cloud copy, newest first
const slotKey = (userId) => `${PLAN_STORAGE_KEY}:${userId}`;
const backupKey = (userId) => `${PLAN_STORAGE_KEY}:backup:${userId}`;
// Backups were once kept in one list for the whole browser, where the next
// account to sign in was offered them. That list cannot be attributed.
const UNFILED_BACKUP_KEY = `${PLAN_STORAGE_KEY}:backup`;
const backupsOf = (userId) => (userId ? readBackups(readJSON(backupKey(userId))) : []);

function readJSON(key) {
  try {
    const saved = localStorage.getItem(key);
    return saved ? JSON.parse(saved) : null;
  } catch {
    return null;
  }
}

function writeJSON(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
    return true;
  } catch {
    return false;
  }
}

function removeKey(key) {
  try {
    localStorage.removeItem(key);
  } catch {
    // Storage unavailable: nothing to remove.
  }
}

export { DEFAULT_PROFILE, BLANK_PROFILE, STARTER_PROFILE, DEFAULT_CONFIG, DEFAULT_DRAWDOWN_PHASES };

const PERSISTED_FIELDS = ['planId', 'planName', 'savedPlans', 'profile', 'simulationConfig', 'simulationResults', 'drawdownPhases', 'selectedHeatmapAge', 'monteCarloScenario'];

function toPersisted(state) {
  const plan = { schemaVersion: PLAN_SCHEMA_VERSION };
  PERSISTED_FIELDS.forEach((field) => { plan[field] = state[field]; });
  return plan;
}

function toCloudPayload(state) {
  // Sampled paths are large and can be regenerated, so they stay local.
  const { allPaths, ...resultsSummary } = state.simulationResults || {};
  return { ...toPersisted(state), simulationResults: state.simulationResults ? resultsSummary : null };
}

// Derived results are never restored from storage: they are recomputed.
const asState = (plan) => ({ ...normalizePlan(plan), heatmapData: [], spendDownAnalysis: null });

function buildInitialState() {
  return asState(readJSON(PLAN_STORAGE_KEY));
}

const totalAccounts = (accounts) => Object.values(accounts || {}).reduce((sum, v) => sum + (Number(v) || 0), 0);

function financialPlanReducer(state, action) {
  switch (action.type) {
    case 'SET_PROFILE': {
      const payload = action.payload;
      const newProfile = { ...state.profile };
      const nestedKeys = ['spouse1', 'spouse2', 'accounts', 'pension1', 'pension2', 'strategySettings', 'glide', 'mortgage', 'longTermCare', 'annuity', 'home'];
      for (const key of Object.keys(payload)) {
        if (nestedKeys.includes(key) && typeof payload[key] === 'object' && payload[key] !== null) {
          newProfile[key] = { ...state.profile[key], ...payload[key] };
        } else {
          newProfile[key] = payload[key];
        }
      }
      const newState = { ...state, profile: newProfile };
      if (payload.accounts !== undefined) {
        newState.simulationConfig = { ...newState.simulationConfig, initialInvestment: totalAccounts(newProfile.accounts) };
      }
      if (payload.annualSavings !== undefined && payload.annualSavings !== state.simulationConfig.annualContribution) {
        newState.simulationConfig = { ...newState.simulationConfig, annualContribution: payload.annualSavings };
      }
      if (payload.savingsGrowthRate !== undefined && payload.savingsGrowthRate !== state.simulationConfig.savingsGrowthRate) {
        newState.simulationConfig = { ...newState.simulationConfig, savingsGrowthRate: payload.savingsGrowthRate };
      }
      return newState;
    }

    case 'SET_PROFILE_ACCOUNTS': {
      const accounts = { ...state.profile.accounts, ...action.payload };
      return {
        ...state,
        profile: { ...state.profile, accounts },
        simulationConfig: { ...state.simulationConfig, initialInvestment: totalAccounts(accounts) },
      };
    }

    case 'SET_SIMULATION_CONFIG': {
      const newState = { ...state, simulationConfig: { ...state.simulationConfig, ...action.payload } };
      // Results from a different allocation would feed the wrong growth rate downstream.
      if (action.payload.riskProfile !== undefined && action.payload.riskProfile !== state.simulationConfig.riskProfile) {
        newState.simulationResults = null;
      }
      if (action.payload.annualContribution !== undefined && action.payload.annualContribution !== state.profile.annualSavings) {
        newState.profile = { ...newState.profile, annualSavings: action.payload.annualContribution };
      }
      if (action.payload.savingsGrowthRate !== undefined && action.payload.savingsGrowthRate !== state.profile.savingsGrowthRate) {
        newState.profile = { ...newState.profile, savingsGrowthRate: action.payload.savingsGrowthRate };
      }
      return newState;
    }

    case 'SET_SIMULATION_RESULTS':
      return { ...state, simulationResults: action.payload };

    case 'SET_DRAWDOWN_PHASES':
      return { ...state, drawdownPhases: action.payload };

    case 'SET_HEATMAP_DATA':
      return { ...state, heatmapData: action.payload };

    case 'SET_HEATMAP_AGE':
      return { ...state, selectedHeatmapAge: action.payload };

    case 'SET_MC_SCENARIO':
      return { ...state, monteCarloScenario: action.payload };

    case 'SET_SPEND_DOWN_ANALYSIS':
      return { ...state, spendDownAnalysis: action.payload };

    case 'SYNC_FROM_SIMULATION': {
      const { config, results } = action.payload;
      return {
        ...state,
        simulationConfig: config,
        simulationResults: results,
      };
    }

    // Several named plans share one saved document; see utils/planLibrary.js.
    case 'CREATE_PLAN':
      return createPlan(state, action.payload);
    case 'SWITCH_PLAN':
      return switchPlan(state, action.payload);
    case 'RENAME_PLAN':
      return renamePlan(state, action.payload.id, action.payload.name);
    case 'DELETE_PLAN':
      return deletePlan(state, action.payload);

    // Swap in a whole plan (cloud copy, another account's copy, a backup, or
    // defaults). Anything computed from the previous plan is dropped with it.
    // `marker` identifies a plan that was loaded rather than edited, so the
    // save logic can tell the two apart however the renders are batched.
    case 'REPLACE':
      return { ...asState(action.payload), loadMarker: action.marker ?? state.loadMarker };

    default:
      return state;
  }
}

const RETRY_FIRST_MS = 30000;
const RETRY_MAX_MS = 300000;

export function FinancialPlanProvider({ children }) {
  const [state, rawDispatch] = useReducer(financialPlanReducer, null, buildInitialState);
  // What the screens use. Notes, once per visit, that the person changed their
  // numbers (never which numbers or what to), so onboarding can be measured.
  const editNoted = useRef(false);
  const dispatch = useCallback((action) => {
    if (!editNoted.current && (action?.type === 'SET_PROFILE' || action?.type === 'SET_PROFILE_ACCOUNTS')) {
      editNoted.current = true;
      track('numbers_changed');
    }
    rawDispatch(action);
  }, []);
  const { user, isAuthenticated, loadFinancialData, saveFinancialData, wasAccountDeleted } = useAuth();
  // Cloud sync is the one feature with a per-user server cost, so it is Pro.
  const { isPro } = useEntitlement();
  const signedInId = isAuthenticated && user?.id ? user.id : null;
  const owner = signedInId && isPro ? signedInId : null;

  // 'off' | 'loading' | 'pending' | 'saved' | 'error'
  const [syncState, setSyncState] = useState('off');
  // Backups belong to the signed-in account; signed out, there are none to offer.
  const [backupState, setBackupState] = useState(() => ({ owner: signedInId, list: backupsOf(signedInId) }));
  let backups = backupState.list;
  if (backupState.owner !== signedInId) {
    backups = backupsOf(signedInId);
    setBackupState({ owner: signedInId, list: backups });
  }
  useEffect(() => removeKey(UNFILED_BACKUP_KEY), []);
  const [localSaveFailed, setLocalSaveFailed] = useState(false);
  const [retryTick, setRetryTick] = useState(0);

  // Whose plan is in memory right now: a user id, or null for the device plan.
  const activeOwner = useRef(null);
  const stateRef = useRef(state);
  stateRef.current = state;
  const signedInRef = useRef(signedInId);
  signedInRef.current = signedInId;
  // True while this device holds changes the cloud has not confirmed.
  const unsaved = useRef(false);
  // Bumped by every edit, so a save can tell whether it is still current.
  const editSeq = useRef(0);
  // Server stamp of the cloud version this copy was last in step with.
  const base = useRef(null);
  // False until the account's cloud copy has been read in this session.
  const reconciled = useRef(false);
  const saving = useRef(false);
  const saveQueued = useRef(false);
  const retryDelay = useRef(RETRY_FIRST_MS);
  // Loading a plan is not an edit. Each load carries a new marker; a state
  // whose marker has not been seen yet came from a load.
  const markerSeq = useRef(0);
  const seenMarker = useRef(undefined);
  const hasRendered = useRef(false);
  const loadPlan = useCallback((plan) => {
    rawDispatch({ type: 'REPLACE', payload: plan, marker: ++markerSeq.current });
  }, []);
  const lastPersisted = useRef(null);
  const localTimer = useRef(null);
  const cloudTimer = useRef(null);
  const pushRef = useRef(null);

  const persistLocal = useCallback(() => {
    const uid = activeOwner.current;
    const plan = toPersisted(stateRef.current);
    const ok = uid
      ? writeJSON(slotKey(uid), { plan, meta: { dirty: unsaved.current, base: base.current, updatedAt: Date.now() } })
      : writeJSON(PLAN_STORAGE_KEY, plan);
    setLocalSaveFailed(!ok);
  }, []);

  const changeBackups = useCallback((userId, change) => {
    if (!userId) return;
    const next = change(backupsOf(userId));
    writeJSON(backupKey(userId), next);
    setBackupState((current) => (current.owner === userId ? { owner: userId, list: next } : current));
  }, []);

  const keepBackup = useCallback((plan, userId) => {
    changeBackups(userId, (list) => addBackup(list, plan));
  }, [changeBackups]);

  // Replace what is on screen with the cloud copy.
  const adoptCloud = useCallback((cloud) => {
    base.current = cloud.stamp;
    unsaved.current = false;
    lastPersisted.current = null;
    loadPlan(cloud.data);
    setSyncState('saved');
  }, [loadPlan]);

  const pushNow = useCallback(async () => {
    const uid = activeOwner.current;
    if (!uid) return;
    // One upload at a time: a second one waits and sends the latest plan.
    if (saving.current) {
      saveQueued.current = true;
      return;
    }
    saving.current = true;
    const seq = editSeq.current;
    setSyncState('pending');
    try {
      const { stamp } = await saveFinancialData(toCloudPayload(stateRef.current), { userId: uid, base: base.current });
      if (activeOwner.current !== uid) return;
      base.current = stamp;
      retryDelay.current = RETRY_FIRST_MS;
      // An edit made while this upload was in flight still needs its own.
      if (seq === editSeq.current) {
        unsaved.current = false;
        setSyncState('saved');
      } else {
        saveQueued.current = true;
      }
      persistLocal();
    } catch (err) {
      if (activeOwner.current !== uid) return;
      if (err?.conflict) {
        // Another device saved first. Its version becomes current; this
        // device's version is kept as a backup the user can restore.
        try {
          const cloud = await loadFinancialData();
          if (activeOwner.current !== uid) return;
          if (cloud) {
            keepBackup(toPersisted(stateRef.current), uid);
            saveQueued.current = false;
            adoptCloud(cloud);
          } else {
            base.current = null;
            saveQueued.current = true;
          }
        } catch {
          setSyncState('error');
        }
      } else {
        setSyncState('error');
        clearTimeout(cloudTimer.current);
        cloudTimer.current = setTimeout(() => pushRef.current?.(), retryDelay.current);
        retryDelay.current = Math.min(retryDelay.current * 2, RETRY_MAX_MS);
      }
    } finally {
      saving.current = false;
      if (saveQueued.current) {
        saveQueued.current = false;
        if (activeOwner.current === uid && unsaved.current) pushRef.current?.();
      }
    }
  }, [saveFinancialData, loadFinancialData, persistLocal, keepBackup, adoptCloud]);
  pushRef.current = pushNow;

  // Switch plans when the syncing account changes (sign-in, sign-out, a new
  // subscription, or a lapsed one), and read the account's cloud copy.
  useEffect(() => {
    let cancelled = false;

    if (activeOwner.current && activeOwner.current !== owner) {
      const previous = activeOwner.current;
      clearTimeout(cloudTimer.current);
      clearTimeout(localTimer.current);
      const deleted = wasAccountDeleted?.(previous);
      // Unsynced edits stay in the account's own slot and are uploaded the
      // next time it syncs. A fully synced copy does not need to stay.
      if (unsaved.current && !deleted) persistLocal(); else removeKey(slotKey(previous));
      activeOwner.current = null;
      unsaved.current = false;
      base.current = null;
      reconciled.current = false;
      editSeq.current++;
      lastPersisted.current = null;
      if (signedInRef.current === previous && !deleted) {
        // Same person, sync turned off (for example a lapsed subscription):
        // the plan they are looking at simply becomes the device plan.
        writeJSON(PLAN_STORAGE_KEY, toPersisted(stateRef.current));
      } else {
        // Someone signed out: nothing of theirs stays on screen.
        loadPlan(readJSON(PLAN_STORAGE_KEY));
      }
    }

    if (!owner) {
      setSyncState('off');
      return undefined;
    }

    if (activeOwner.current !== owner) {
      // A working copy left by this account is shown at once, before (and
      // whether or not) the server can be reached.
      const local = readJSON(slotKey(owner));
      if (local?.plan) {
        activeOwner.current = owner;
        unsaved.current = !!local.meta?.dirty;
        base.current = local.meta?.base ?? null;
        reconciled.current = false;
        lastPersisted.current = null;
        loadPlan(local.plan);
      }
    }
    if (activeOwner.current === owner && reconciled.current) return undefined;

    setSyncState('loading');
    loadFinancialData().then((cloud) => {
      if (cancelled) return;
      if (activeOwner.current === owner) {
        reconciled.current = true;
        const action = reconcileAccountCopy({ cloud, dirty: unsaved.current, base: base.current });
        if (action === 'push') {
          if (!cloud) base.current = null;
          unsaved.current = true;
          pushNow();
        } else if (action === 'keep') {
          setSyncState('saved');
        } else {
          if (action === 'takeCloudBackupLocal') keepBackup(toPersisted(stateRef.current), owner);
          adoptCloud(cloud);
        }
        return;
      }

      // First sync for this account on this device.
      const devicePlan = toPersisted(stateRef.current);
      const decision = resolveFirstSync({ cloud, devicePlan });
      if (decision.backupDevice) keepBackup(devicePlan, owner);
      // The device plan now belongs to the account (adopted, backed up, or
      // identical to the cloud copy), so it does not stay behind for the next
      // person to open the app signed out.
      removeKey(PLAN_STORAGE_KEY);
      activeOwner.current = owner;
      reconciled.current = true;
      lastPersisted.current = null;
      if (decision.use === 'cloud') {
        adoptCloud(cloud);
      } else {
        base.current = null;
        unsaved.current = true;
        persistLocal();
        pushNow();
      }
    }).catch(() => {
      // Whatever is on screen stays in use and untouched. Nothing is uploaded
      // over a cloud copy that has not been read; retried on resume or request.
      if (!cancelled) setSyncState('error');
    });

    return () => { cancelled = true; };
  }, [owner, retryTick, loadFinancialData, pushNow, persistLocal, keepBackup, adoptCloud, loadPlan, wasAccountDeleted]);

  // Save on every change to a persisted field: to the device shortly after,
  // and to the cloud once the user pauses.
  useEffect(() => {
    const fields = PERSISTED_FIELDS.map((field) => state[field]);
    const previous = lastPersisted.current;
    lastPersisted.current = fields;
    if (previous && fields.every((value, i) => value === previous[i])) return;

    const wasCloudLoad = state.loadMarker !== seenMarker.current;
    seenMarker.current = state.loadMarker;
    // The state the app starts with is not an edit either.
    const initialRender = !hasRendered.current;
    hasRendered.current = true;

    clearTimeout(localTimer.current);
    localTimer.current = setTimeout(persistLocal, 300);

    if (activeOwner.current && !wasCloudLoad && !initialRender) {
      unsaved.current = true;
      editSeq.current++;
      // Until the cloud copy has been read there is nothing safe to upload against.
      if (reconciled.current) {
        setSyncState('pending');
        clearTimeout(cloudTimer.current);
        cloudTimer.current = setTimeout(() => pushRef.current?.(), 5000);
      }
    }
  }, [state, persistLocal]);

  useEffect(() => () => {
    clearTimeout(localTimer.current);
    clearTimeout(cloudTimer.current);
  }, []);

  // Leaving the app: write everything now. Coming back: pick up changes made
  // on another device, or finish a save that did not complete.
  useEffect(() => {
    const onHide = () => {
      clearTimeout(localTimer.current);
      persistLocal();
      if (activeOwner.current && unsaved.current && reconciled.current) {
        clearTimeout(cloudTimer.current);
        pushNow();
      }
    };
    const onShow = async () => {
      const uid = activeOwner.current;
      if (!uid || !reconciled.current) {
        if (owner) setRetryTick((tick) => tick + 1);
        return;
      }
      if (unsaved.current) {
        // The upload is conditional on the cloud version, so a change made
        // elsewhere in the meantime is detected rather than overwritten.
        pushNow();
        return;
      }
      try {
        const cloud = await loadFinancialData();
        if (activeOwner.current !== uid || unsaved.current || !cloud) return;
        if (cloud.stamp !== base.current) adoptCloud(cloud);
      } catch {
        // Offline: the local copy stays in use.
      }
    };
    const onVisibility = () => (document.visibilityState === 'hidden' ? onHide() : onShow());

    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', onHide);
    window.addEventListener('online', onShow);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', onHide);
      window.removeEventListener('online', onShow);
    };
  }, [owner, loadFinancialData, pushNow, persistLocal, adoptCloud]);

  // Both work from the list as it is when they run, so a backup added a
  // moment earlier is never dropped.
  const dropNewestBackup = useCallback(() => {
    changeBackups(signedInRef.current, (list) => list.slice(1));
  }, [changeBackups]);

  const restoreBackup = useCallback(() => {
    const latest = backups[0];
    if (!latest) return;
    rawDispatch({ type: 'REPLACE', payload: latest.plan });
    dropNewestBackup();
  }, [backups, dropNewestBackup]);

  const discardBackup = dropNewestBackup;

  const retrySync = useCallback(() => {
    if (activeOwner.current && reconciled.current) pushNow(); else setRetryTick((tick) => tick + 1);
  }, [pushNow]);

  const contextValue = React.useMemo(() => ({
    state,
    dispatch,
    sync: {
      state: syncState,
      backup: backups[0] || null,
      backupCount: backups.length,
      restoreBackup,
      discardBackup,
      retry: retrySync,
      localSaveFailed,
    },
  }), [state, dispatch, syncState, backups, restoreBackup, discardBackup, retrySync, localSaveFailed]);

  return (
    <FinancialPlanContext.Provider value={contextValue}>
      {children}
    </FinancialPlanContext.Provider>
  );
}

export function useFinancialPlan() {
  const context = useContext(FinancialPlanContext);
  if (!context) {
    throw new Error('useFinancialPlan must be used within a FinancialPlanProvider');
  }
  return context;
}
